import { useState, useEffect } from 'react';
import { settingsApi, authApi, formatApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Phone, 
  Users, 
  Plus, 
  Loader2, 
  Save,
  Shield,
  Webhook,
  Copy,
  CheckCircle,
  Lock,
  Eye,
  EyeOff,
  Pencil,
  Trash2,
  LogIn,
  UserCog
} from 'lucide-react';
import { toast } from 'sonner';

export default function Settings() {
  const { isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [voipSettings, setVoipSettings] = useState({
    webhook_url: '',
    api_key: '',
    enabled: false
  });
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: '', new_password: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'staff'
  });
  const [editingUser, setEditingUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Production webhook URL - will be used when deployed to voicebook.telcavoip.ai
  const webhookEndpoint = `https://voicebook.telcavoip.ai/api/voip/webhook`;

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const [voipRes, usersRes] = await Promise.all([
        settingsApi.getVoip(),
        authApi.listUsers()
      ]);
      setVoipSettings(voipRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      console.error('Error loading settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVoip = async () => {
    setSaving(true);
    try {
      await settingsApi.updateVoip(voipSettings);
      toast.success('Impostazioni VoIP salvate');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await authApi.createUser(newUser);
      toast.success('Utente creato');
      setUserDialogOpen(false);
      setNewUser({ name: '', email: '', password: '', role: 'staff' });
      loadSettings();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookEndpoint);
    setCopied(true);
    toast.success('URL copiato negli appunti');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    setSaving(true);
    try {
      const payload = { name: editingUser.name, email: editingUser.email, role: editingUser.role, is_active: editingUser.is_active !== false };
      if (editingUser.newPassword) payload.password = editingUser.newPassword;
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users/${editingUser.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Errore'); }
      toast.success('Utente aggiornato');
      setEditDialogOpen(false);
      setEditingUser(null);
      loadSettings();
    } catch (err) {
      toast.error(err.message);
    } finally { setSaving(false); }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users/${userId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Errore'); }
      toast.success('Utente eliminato');
      setDeleteConfirm(null);
      loadSettings();
    } catch (err) { toast.error(err.message); }
  };

  const handleImpersonate = async (userId) => {
    try {
      const token = localStorage.getItem('access_token');
      // Save current superadmin token for return
      localStorage.setItem('superadmin_token', token);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/impersonate/${userId}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Errore'); }
      const data = await res.json();
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('impersonating', JSON.stringify({ name: data.name, email: data.email, role: data.role }));
      toast.success(data.message);
      window.location.href = '/';
    } catch (err) { toast.error(err.message); }
  };

  if (loading) {
    return (
      <Layout>
        <PageHeader title="Impostazioni" subtitle="Configurazione del sistema" />
        <div className="content-area">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader 
        title="Impostazioni" 
        subtitle="Configurazione del sistema e integrazione VoIP"
      />

      <div className="content-area space-y-6" data-testid="settings-page">
        {/* Change Password */}
        <div className="bg-white border border-slate-200/60 rounded-lg">
          <div className="px-6 py-4 border-b border-slate-200/60 flex items-center gap-3">
            <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
              <Lock className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>Cambia Password</h3>
              <p className="text-sm text-slate-500">Aggiorna la tua password di accesso</p>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <Label>Password attuale</Label>
              <Input type="password" value={pwdForm.current} onChange={(e) => setPwdForm({ ...pwdForm, current: e.target.value })}
                placeholder="Inserisci la password attuale" className="mt-1.5" data-testid="current-password" />
            </div>
            <div>
              <Label>Nuova password</Label>
              <Input type="password" value={pwdForm.new_password} onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })}
                placeholder="Minimo 8 caratteri" className="mt-1.5" data-testid="new-password" />
            </div>
            <div>
              <Label>Conferma nuova password</Label>
              <Input type="password" value={pwdForm.confirm} onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })}
                placeholder="Ripeti la nuova password" className="mt-1.5" data-testid="confirm-password" />
            </div>
            <Button
              onClick={async () => {
                if (pwdForm.new_password !== pwdForm.confirm) { toast.error('Le password non corrispondono'); return; }
                if (pwdForm.new_password.length < 8) { toast.error('La password deve avere almeno 8 caratteri'); return; }
                setPwdSaving(true);
                try {
                  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/change-password`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
                    body: JSON.stringify({ current_password: pwdForm.current, new_password: pwdForm.new_password }),
                  });
                  const data = await res.json();
                  if (res.ok) { toast.success('Password aggiornata!'); setPwdForm({ current: '', new_password: '', confirm: '' }); }
                  else { toast.error(data.detail || 'Errore'); }
                } catch { toast.error('Errore di rete'); }
                finally { setPwdSaving(false); }
              }}
              disabled={pwdSaving || !pwdForm.current || !pwdForm.new_password || !pwdForm.confirm}
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="change-password-btn"
            >
              {pwdSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Aggiorna Password
            </Button>
          </div>
        </div>

        {/* VoIP Integration */}
        <div className="bg-white border border-slate-200/60 rounded-lg">
          <div className="px-6 py-4 border-b border-slate-200/60 flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
              <Webhook className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Integrazione VoIP
              </h3>
              <p className="text-sm text-slate-500">Configurazione webhook per telcavoip</p>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            {/* Webhook URL to share */}
            <div>
              <Label className="text-slate-700">Endpoint Webhook (da configurare su telcavoip)</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  value={webhookEndpoint}
                  readOnly
                  className="font-mono text-sm bg-slate-50"
                  data-testid="webhook-url-display"
                />
                <Button
                  variant="outline"
                  onClick={copyWebhookUrl}
                  data-testid="copy-webhook-btn"
                >
                  {copied ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Configura questo URL nel tuo sistema telcavoip per ricevere le chiamate in ingresso
              </p>
            </div>

            <div>
              <Label htmlFor="api_key">API Key telcavoip (opzionale)</Label>
              <Input
                id="api_key"
                type="password"
                value={voipSettings.api_key}
                onChange={(e) => setVoipSettings({ ...voipSettings, api_key: e.target.value })}
                placeholder="Inserisci la tua API key"
                className="mt-1.5"
                data-testid="voip-api-key"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Switch
                  id="voip_enabled"
                  checked={voipSettings.enabled}
                  onCheckedChange={(checked) => setVoipSettings({ ...voipSettings, enabled: checked })}
                  data-testid="voip-enabled-switch"
                />
                <Label htmlFor="voip_enabled">Sistema VoIP attivo</Label>
              </div>
              <Button 
                onClick={handleSaveVoip} 
                disabled={saving}
                className="bg-sky-500 hover:bg-sky-600"
                data-testid="save-voip-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Salva
              </Button>
            </div>
          </div>
        </div>

        {/* API Documentation */}
        <div className="bg-white border border-slate-200/60 rounded-lg">
          <div className="px-6 py-4 border-b border-slate-200/60 flex items-center gap-3">
            <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
              <Phone className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Documentazione API Webhook
              </h3>
              <p className="text-sm text-slate-500">Come integrare il tuo sistema VoIP</p>
            </div>
          </div>
          
          <div className="p-6">
            <div className="bg-slate-900 rounded-lg p-4 text-sm font-mono text-slate-300 overflow-x-auto">
              <p className="text-emerald-400">// Endpoint: POST {webhookEndpoint}</p>
              <p className="mt-2 text-slate-500">// Request body:</p>
              <pre className="text-amber-400">{`{
  "caller_phone": "+39 333 1234567",
  "call_id": "unique-call-id",
  "event_type": "incoming|audio_received|hangup",
  "audio_data": "base64-encoded-audio",  // per audio_received
  "additional_data": {}  // dati extra opzionali
}`}</pre>
              <p className="mt-4 text-slate-500">// Response:</p>
              <pre className="text-sky-400">{`{
  "response_text": "Messaggio da leggere",
  "audio_base64": "base64-audio-response",
  "continue_call": true,
  "appointment_created": false
}`}</pre>
            </div>
          </div>
        </div>

        {/* User Management */}
        {isSuperAdmin && (
          <div className="bg-white border border-slate-200/60 rounded-lg">
            <div className="px-6 py-4 border-b border-slate-200/60 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                    Gestione Utenti
                  </h3>
                  <p className="text-sm text-slate-500">Amministratori e operatori del sistema</p>
                </div>
              </div>
              <Button 
                onClick={() => setUserDialogOpen(true)}
                className="bg-sky-500 hover:bg-sky-600"
                data-testid="add-user-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Utente
              </Button>
            </div>
            
            <Table>
              <TableHeader className="table-header">
                <TableRow>
                  <TableHead>Utente</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Ruolo</TableHead>
                  <TableHead>Data Creazione</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-slate-100 rounded-full flex items-center justify-center">
                          {u.role === 'superadmin' ? (
                            <Shield className="w-4 h-4 text-amber-600" />
                          ) : (
                            <span className="text-sm font-medium text-slate-600">
                              {u.name?.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <span className="font-medium text-slate-800">{u.name}</span>
                          {u.two_factor_enabled && <span className="ml-2 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">2FA</span>}
                          {u.is_active === false && <span className="ml-2 text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">Disattivo</span>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{u.email}</TableCell>
                    <TableCell>
                      <span className={`badge ${
                        u.role === 'superadmin' ? 'bg-amber-100 text-amber-700' :
                        u.role === 'partner' ? 'bg-sky-100 text-sky-700' :
                        u.role === 'admin' ? 'bg-violet-100 text-violet-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {u.role === 'superadmin' ? 'Super Admin' :
                         u.role === 'partner' ? 'Partner' :
                         u.role === 'admin' ? 'Admin' : 'Staff'}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {u.created_at && new Date(u.created_at).toLocaleDateString('it-IT')}
                    </TableCell>
                    <TableCell className="text-right">
                      {u.role !== 'superadmin' && (
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleImpersonate(u.id)} title="Accedi come questo utente"
                            className="p-1.5 rounded-lg text-sky-500 hover:bg-sky-50 transition" data-testid={`impersonate-${u.id}`}>
                            <LogIn className="w-4 h-4" />
                          </button>
                          <button onClick={() => { setEditingUser({...u, newPassword: ''}); setEditDialogOpen(true); }} title="Modifica"
                            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 transition" data-testid={`edit-user-${u.id}`}>
                            <Pencil className="w-4 h-4" />
                          </button>
                          {deleteConfirm === u.id ? (
                            <div className="flex items-center gap-1">
                              <button onClick={() => handleDeleteUser(u.id)} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600">Conferma</button>
                              <button onClick={() => setDeleteConfirm(null)} className="px-2 py-1 text-xs bg-slate-200 text-slate-600 rounded">No</button>
                            </div>
                          ) : (
                            <button onClick={() => setDeleteConfirm(u.id)} title="Elimina"
                              className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition" data-testid={`delete-user-${u.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* New User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="new-user-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              Nuovo Utente
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="user_name">Nome *</Label>
              <Input
                id="user_name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="Nome completo"
                required
                className="mt-1.5"
                data-testid="user-name-input"
              />
            </div>
            <div>
              <Label htmlFor="user_email">Email *</Label>
              <Input
                id="user_email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="email@esempio.it"
                required
                className="mt-1.5"
                data-testid="user-email-input"
              />
            </div>
            <div>
              <Label htmlFor="user_password">Password *</Label>
              <Input
                id="user_password"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Minimo 8 caratteri"
                required
                className="mt-1.5"
                data-testid="user-password-input"
              />
            </div>
            <div>
              <Label htmlFor="user_role">Ruolo *</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value })}
              >
                <SelectTrigger className="mt-1.5" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="superadmin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)}>
                Annulla
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-sky-500 hover:bg-sky-600"
                data-testid="create-user-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Crea Utente
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              <UserCog className="w-5 h-5 inline mr-2 text-sky-500" />
              Modifica Utente
            </DialogTitle>
          </DialogHeader>
          {editingUser && (
            <form onSubmit={handleEditUser} className="space-y-4 mt-4">
              <div>
                <Label>Nome</Label>
                <Input value={editingUser.name || ''} onChange={e => setEditingUser({...editingUser, name: e.target.value})} required />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={editingUser.email || ''} onChange={e => setEditingUser({...editingUser, email: e.target.value})} required />
              </div>
              <div>
                <Label>Ruolo</Label>
                <Select value={editingUser.role} onValueChange={v => setEditingUser({...editingUser, role: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="partner">Partner</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="receptionist">Receptionist</SelectItem>
                    <SelectItem value="doctor">Medico</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nuova Password (lascia vuoto per non cambiare)</Label>
                <Input type="password" value={editingUser.newPassword || ''} onChange={e => setEditingUser({...editingUser, newPassword: e.target.value})} placeholder="••••••••" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editingUser.is_active !== false} onCheckedChange={v => setEditingUser({...editingUser, is_active: v})} />
                <Label>Account attivo</Label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Annulla</Button>
                <Button type="submit" disabled={saving} className="bg-sky-500 hover:bg-sky-600">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Salva
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
